import {
    BannerMessage,
    ButtonNew,
    DragAndDropFileUpload,
    LuneThemeNew,
    Modal,
    Text,
} from '@lune-fe/lune-ui-lib'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { Box, Link, Stack } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { FileRejection } from 'react-dropzone'

import { uploadDataSheet } from 'endpoints/dapi'
import useDataSheets from 'hooks/useDataSheets'

import { handleSheetUploadError, MAX_SIZE_BYTES, MAX_SIZE_HUMAN_READABLE } from './CsvSheetCommon'

const { spacing } = LuneThemeNew

type CsvSheetUploadModalProps = {
    open: boolean
    onClose: (isSuccess: boolean) => void
}

const FintechSheetUploadModal: FC<CsvSheetUploadModalProps> = ({ open, onClose }) => {
    const [isUploading, setUploading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const ref = React.useRef<HTMLDivElement>(null)
    const type = 'fintech'
    const { refetch } = useDataSheets(type)

    const onUploadSheet = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        if (rejectedFiles.length) {
            setModalHeader(`CSV files only, up to ${MAX_SIZE_HUMAN_READABLE}`)
            setModalSubHeader(undefined)
            return
        }

        const formData = new FormData()
        const file = acceptedFiles[0]
        formData.append('sheet', file)
        setUploading(true)
        uploadDataSheet(formData, type)
            .then(() => {
                setIsSuccess(true)
                setModalHeader('Upload complete')
                setModalSubHeader(` Processing your file, we'll email you when ready.
                            Should take a few minutes`)
                setUploading(false)
                refetch()
            })
            .catch((err) => {
                const errorInfo = handleSheetUploadError(err)
                setModalHeader(errorInfo.subject.join('. '))
                setModalSubHeader(undefined)
                setUploading(false)
            })
    }

    const handleClose = () => {
        onClose(isSuccess)
    }

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setIsSuccess(false)
            setUploading(false)
        }
    }, [open])

    const uploadContent = (
        <>
            <Box sx={{ height: spacing(25), width: '100%' }}>
                <DragAndDropFileUpload
                    ref={ref}
                    accept={{ 'text/csv': ['.csv'] }}
                    maxSize={MAX_SIZE_BYTES}
                    isUploading={isUploading}
                    onDrop={onUploadSheet}
                    rejectDescription={`CSV files only, up to ${MAX_SIZE_HUMAN_READABLE}`}
                    description={
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <FileUploadOutlinedIcon
                                sx={{
                                    width: '32px',
                                    height: '32px',
                                }}
                            />
                            <Text variant={'body2'}>
                                Drag and drop your file here or{' '}
                                <Link
                                    onClick={() => {
                                        if (ref.current) {
                                            ref.current.click()
                                        }
                                    }}
                                    color={'TextPrimary'}
                                    fontWeight={'medium'}
                                >
                                    browse files
                                </Link>
                            </Text>
                            <Text variant={'body3'} color={'TextTertiary'}>
                                .csv up to {MAX_SIZE_HUMAN_READABLE}
                            </Text>
                        </Box>
                    }
                />
            </Box>
            <BannerMessage
                message={`Calculations will count towards your tier limit. By setting the "limit" column to a value greater than one, a row will return multiple calculations`}
            />
        </>
    )

    const afterUploadContent = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
            }}
        >
            <ButtonNew onClick={() => handleClose()}>OK</ButtonNew>
        </Box>
    )

    const [modalHeader, setModalHeader] = useState<string>(`Fintech spreadsheet calculations`)
    const [modalSubHeader, setModalSubHeader] = useState<string | undefined>(
        `Upload your fintech data using our template.`,
    )

    return (
        <Modal
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={handleClose}
            header={<Text variant={'h5'}>{modalHeader}</Text>}
            subheader={
                <Text variant={'body2'} sx={{ color: `Grey700`, display: 'block' }} mt={1}>
                    {modalSubHeader}
                </Text>
            }
        >
            <Stack
                spacing={{ xs: 4 }}
                sx={{
                    width: '100%',
                }}
            >
                {isSuccess ? afterUploadContent : uploadContent}
            </Stack>
        </Modal>
    )
}

export default FintechSheetUploadModal
